import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
    BalansVoorziening,
    BokkzVpbDataService,
    CorporateTaxEntity,
} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {debounceTime, filter, Observable, shareReplay, tap} from 'rxjs';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {CommonModule, DecimalPipe} from '@angular/common';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
    selector: 'snelstart-venture-fiscaal-feature-spec-overige-voorziening',
    templateUrl: './spec-overige-voorziening.component.html',
    styleUrls: ['./spec-overige-voorziening.component.scss'],
    imports: [
        SharedUiDialogModule,
        ReactiveFormsModule,
        SharedUiDetailsModule,
        SharedUiFormControlsModule,
        SharedUiAlertModule,
        DecimalPipe,
        SharedUiButtonModule,
        CommonModule,
    ],
})
export class SpecOverigeVoorzieningComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef = inject(DestroyRef);
  public dialogVisible = false;

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      tap(data => {
        if (data.aangifte?.balansEnResultatenrekening?.overigeVoorzieningen?.at(0)) {
          this.voorzieningFg.patchValue(data.aangifte.balansEnResultatenrekening.overigeVoorzieningen[0], {emitEvent: false});
        }
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public voorzieningFg = new FormGroup({
    omschrijving: new FormControl<string | null>(null),
    openingsbalans: new FormControl<number | null>(null),
    slotbalans: new FormControl<number | null>(null),
    toelichting: new FormControl<string | null>("Totale mutatie overige voorzieningen"),
    dotatie: new FormControl<number | null>(null),
    onttrekking: new FormControl<number | null>(null),
  })

  showDialog() {
    this.dialogVisible = true;
  }

  ngOnInit(): void {
    this.corporateTaxEntity$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();

    this.voorzieningFg.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.saveForm();
    });
  }

  ngOnDestroy(): void {
    this.saveForm();
  }

  getTotaal(): number {
    const start = this.voorzieningFg.get('openingsbalans')?.value || 0
    const dotatie = this.voorzieningFg.get('dotatie')?.value || 0
    const onttrekking = this.voorzieningFg.get('onttrekking')?.value || 0
    const einde = this.voorzieningFg.get('slotbalans')?.value || 0
    return einde + onttrekking - dotatie - start;
  }

  saveForm(): void {
    this.voorzieningFg.updateValueAndValidity({emitEvent: false});

    const formValue = this.voorzieningFg.getRawValue();
    const specificatie: BalansVoorziening = {
      omschrijving: formValue.omschrijving?.trim() || undefined,
      openingsbalans: formValue.openingsbalans ?? undefined,
      slotbalans: formValue.slotbalans ?? undefined,
      toelichting: formValue.toelichting ?? undefined,
      dotatie: formValue.dotatie ?? undefined,
      onttrekking: formValue.onttrekking ?? undefined,
    };

    this.dataService.patchCorporateTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      const huidigeSpecificatie = currentEntity.aangifte.balansEnResultatenrekening?.overigeVoorzieningen?.[0];

      const mergedSpecificatie: BalansVoorziening = {
        ...huidigeSpecificatie,
        ...specificatie,
        // Preserve the main balance values when only the detailed specification is edited.
        openingsbalans: specificatie.openingsbalans ?? huidigeSpecificatie?.openingsbalans,
        slotbalans: specificatie.slotbalans ?? huidigeSpecificatie?.slotbalans,
      };

      const hasData = Object.values(mergedSpecificatie).some(value => value !== undefined);

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          balansEnResultatenrekening: {
            ...currentEntity.aangifte.balansEnResultatenrekening,
            overigeVoorzieningen: hasData ? [mergedSpecificatie] : undefined,
          },
        },
      } as CorporateTaxEntity;
    });
  }
}
